//#region Regions (select & search)

const DEFAULT_SEARCH_REGION = {
    code: "cs",
    label: "Česká republika",
    flag_id: "cz"
};
// !IMPORTANT - also update RegionSelect
const SUPPORTED_SEARCH_REGIONS = [
    DEFAULT_SEARCH_REGION,
];

const getValidSearchRegionCode = (region) => {
    if (!region) return DEFAULT_SEARCH_REGION;

    try {
        const normalized = String(region).toLowerCase();

        const foundRegion = SUPPORTED_SEARCH_REGIONS.find(
            r => r.code.toLowerCase() === normalized
        );

        return foundRegion || DEFAULT_SEARCH_REGION;
    } catch (e) {
        return DEFAULT_SEARCH_REGION;
    }
};

// Returns currency if language
// If also any_price it returns the value formatted
function getCurrencySymbol(language, any_price) {
    const DEFAULT_LANG = 'en';

    const currencies_map = {
        cs: 'Kč',
        en: '€',
    }

    const final_lang = String(language).toLocaleLowerCase().trim();
    let cm = currencies_map[final_lang];
    if (!cm) {
        cm = currencies_map[DEFAULT_LANG];
    }

    if (any_price == null) {
        return cm;
    }

    switch (final_lang) {
        case 'cs':
            return `${any_price} ${cm}`;
        default:
            return `${cm}${any_price}`;
    }
}

//#endregion

//#region Languages (web & gopay)

//#endregion

function isCouponInvalid(coupon) {
    if (!coupon) return true;
    if (coupon.expiresAt && coupon.expiresAt?.toMillis() < Date.now()) return true;
    if (typeof coupon.usesLeft === "number" && coupon.usesLeft <= 0) return true;
    if (coupon.inUse && !coupon.global) return true;
    return false;
}

//#region Pricing
function roundFixed(num) {
    if (num === 0) return 0;
    const final = +(Math.round(parseFloat(num) + Number.EPSILON + "e+2") + "e-2");
    return Number(final.toFixed(2));
}

const getPercentSale = (origPrice, currPrice, count) => {
    const totalOrig = origPrice * count;
    const totalCurr = currPrice * count;

    const original = Number(totalOrig);
    const sale = Number(totalCurr);

    if (!Number.isFinite(original) || !Number.isFinite(sale)) return 0;
    if (original <= 0) return 0;
    if (sale >= original || sale < 0) return 0;

    const discount = ((original - sale) / original) * 100;
    return Math.min(100, Math.max(0, Math.round(discount)));
}

const CZ_VAT_RATE = 0.21;
function getPriceForCount(prices, coupon_count = 0, include_vin = false, coupon_sale) {
    if (!Array.isArray(prices) || prices.length === 0) {
        return {};
    }

    const totalCount = coupon_count + (Boolean(include_vin) ? 1 : 0);

    // sort ascending by count just in case
    const sorted = [...prices].sort((a, b) => a.c - b.c);

    const baseTier = sorted[0];

    // find the highest c <= count
    let priceObj = sorted.find(p => p.c >= coupon_count);
    // if count exceeds max c, take the last object
    if (!priceObj) {
        priceObj = sorted[sorted.length - 1];
    }

    const priceMultiplier = 1 - Math.min(1, Math.max(0, coupon_sale ?? 0));
    const tier =
        sorted.find(t => totalCount <= t.c) ||
        sorted[sorted.length - 1];

    const pricePerPiece = roundFixed(tier.p * priceMultiplier);
    const totalPrice = roundFixed(totalCount * pricePerPiece);
    const basePerPiece = roundFixed(baseTier.p);
    const salePercent = getPercentSale(
        basePerPiece,
        pricePerPiece,
        totalCount
    );

    // VAT
    const divisor = 1 + CZ_VAT_RATE;
    const totalPriceNoVat = roundFixed(totalPrice / divisor);

    return {
        totalPrice,
        pricePerPiece,
        basePerPiece,
        salePercent,
        totalPriceNoVat,
    };
}
//#endregion

function isProbablyValidISIC(value) {
    if (!value) return false;

    // remove spaces and dashes
    const normalized = String(value)
        .trim()
        .replace(/[\s-]/g, "")
        .toUpperCase();

    // allow only A-Z and 0-9
    if (!/^[A-Z0-9]+$/.test(normalized)) {
        return false;
    }

    // typical reasonable length
    if (normalized.length < 8 || normalized.length > 20) {
        return false;
    }

    // require at least 4 digits
    const digitCount = (normalized.match(/\d/g) || []).length;

    if (digitCount < 4) {
        return false;
    }

    // reject repeated garbage like AAAAAAAA
    if (/^(.)\1+$/.test(normalized)) {
        return false;
    }

    return true;
}

const ISIC_SALE = 0.1;

const isVinValid = (vin_text) => {
    if (!vin_text || typeof vin_text !== "string") return false;
    const vin = vin_text.trim().toUpperCase();
    if (vin.length !== 17) return false;
    return /^[A-HJ-NPR-Z0-9]+$/.test(vin);
}

const isEmailValid = (email) => {
    if (!email || !email.trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        return false;
    }
    return true;
}

/**
 * CommonJS export (Node, Firebase Functions)
 */
module.exports = {
    isCouponInvalid,
    getPriceForCount,
    getValidSearchRegionCode,
    SUPPORTED_SEARCH_REGIONS,
    getCurrencySymbol,
    getPercentSale,
    isProbablyValidISIC,
    ISIC_SALE,
    isVinValid,
    isEmailValid,
};

/**
 * ESM compatibility (Vite, Webpack, React)
 */
module.exports.default = {
    isCouponInvalid,
    getPriceForCount,
    getValidSearchRegionCode,
    SUPPORTED_SEARCH_REGIONS,
    getCurrencySymbol,
    getPercentSale,
    isProbablyValidISIC,
    ISIC_SALE,
    isVinValid,
    isEmailValid,
};

module.exports.getPercentSale = getPercentSale;
module.exports.isCouponInvalid = isCouponInvalid;
module.exports.getPriceForCount = getPriceForCount;

module.exports.getCurrencySymbol = getCurrencySymbol;
module.exports.getValidSearchRegionCode = getValidSearchRegionCode;
module.exports.SUPPORTED_SEARCH_REGIONS = SUPPORTED_SEARCH_REGIONS;

module.exports.isProbablyValidISIC = isProbablyValidISIC;
module.exports.ISIC_SALE = ISIC_SALE;
module.exports.isVinValid = isVinValid;
module.exports.isEmailValid = isEmailValid;